import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaFutbol, FaTrophy, FaUsers, FaClock, FaMapMarkerAlt, FaChevronRight } from 'react-icons/fa';
import founderImage from './images/about-the-founder.jpg';
import logo from './images/logo-long.svg';
import defaultLightLogo from './images/teams/logo-light.png';
import { formatDate, formatTime } from './utils/helpers';
import './scss/matchWelcomePage.scss';

function MatchWelcomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(true);
  
  // Get match ID from URL params (support both 'm' and 'match_id' for backward compatibility)
  const searchParams = new URLSearchParams(location.search);
  const matchId = searchParams.get('m') || searchParams.get('match_id');
  const coachId = searchParams.get('c');
  const publicApiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  
  // Device detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;
  const isDesktop = !isMobile;
  
  // Add body class for this page
  useEffect(() => {
    document.body.classList.add('match-welcome-page');
    
    return () => {
      document.body.classList.remove('match-welcome-page');
    };
  }, []);

  // Fetch match data
  useEffect(() => {
    const fetchMatchData = async () => {
      if (!matchId) {
        setLoadingMatch(false);
        return;
      }

      try {
        const coachQuery = coachId ? `&c=${encodeURIComponent(coachId)}` : '';
        const response = await fetch(
          `${publicApiUrl}/get_public_match_info.php?m=${encodeURIComponent(matchId)}${coachQuery}`
        );
        const data = await response.json();
        
        if (data.success) {
          const match = data.match;
          // Ensure opposition name is set with fallback
          if (!match.opposition || match.opposition === '') {
            match.opposition = 'Opposition Team';
          }
          setMatchData(match);
        } else {
          console.error('Failed to fetch match data:', data.message);
        }
      } catch (error) {
        console.error('Error fetching match data:', error);
      } finally {
        setLoadingMatch(false);
      }
    };

    fetchMatchData();
  }, [matchId, coachId, publicApiUrl]);



  if (loadingMatch) {
    return (
      <div className="match-welcome-page">
        <div className="welcome-container">
          <div className="loading-state">
            <p>Loading match information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!matchData) {
    return (
      <div className="match-welcome-page">
        <div className="welcome-container">
          <div className="error-state">
            <h2>Match Not Found</h2>
            <p>Sorry, we couldn't find the match information.</p>
          </div>
        </div>
      </div>
    );
  }

  const teamLogo = `https://football.pebbledesigns.co.uk/api/images/teams/${matchData.team_id}/logo-dark.png`;
  const oppositionLogo = matchData.opposition_team_id 
    ? `https://football.pebbledesigns.co.uk/api/images/teams/${matchData.opposition_team_id}/logo-dark.png`
    : null;

  return (
    <div className="match-welcome-page">
      <Helmet>
        <title>{`${matchData.team_name} vs ${matchData.opposition} - Match Information`}</title>
        <meta name="description" content={`Match information for ${matchData.team_name} vs ${matchData.opposition} on ${matchData.formatted_date} at ${matchData.formatted_time}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={`${matchData.team_name} vs ${matchData.opposition} - Match Information`} />
        <meta property="og:description" content={`Match information for ${matchData.team_name} vs ${matchData.opposition} on ${matchData.formatted_date} at ${matchData.formatted_time}`} />
        <meta property="og:image" content={teamLogo} />
        <meta property="og:site_name" content="FirstWhistle" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:title" content={`${matchData.team_name} vs ${matchData.opposition} - Match Information`} />
        <meta name="twitter:description" content={`Match information for ${matchData.team_name} vs ${matchData.opposition} on ${matchData.formatted_date} at ${matchData.formatted_time}`} />
        <meta name="twitter:image" content={teamLogo} />
        
        {/* Additional meta tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="FirstWhistle" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      
      <div className="welcome-container">
        {/* Match Confirmation Section */}
        <div className="match-confirmation-section">
          {/* FirstWhistle Logo */}
          <div className="firstwhistle-logo-container">
            <img src={logo} alt="FirstWhistle Logo" className="firstwhistle-logo" />
          </div>
          
          {/* Title */}
          <h1 className="match-confirmation-title">Match Confirmation Details</h1>

          {/* Personalized Message */}
          <div className="match-welcome-message">
            {matchData.opposition_coach?.first_name && matchData.coach?.first_name ? (
              <p>
                Hi <strong>{matchData.opposition_coach.first_name}</strong>, <strong>{matchData.coach.first_name}</strong> has registered a {matchData.match_type?.toLowerCase() || 'friendly'} match against you. Please see the details below.
              </p>
            ) : matchData.coach?.first_name ? (
              <p>
                Hi there, <strong>{matchData.coach.first_name}</strong> has registered a {matchData.match_type?.toLowerCase() || 'friendly'} match against you. Please see the details below.
              </p>
            ) : (
              <p>
                Hi there, we've registered a {matchData.match_type?.toLowerCase() || 'friendly'} match against you. Please see the details below.
              </p>
            )}
            <p className="message-subtext">
              This page contains all the information you need about the upcoming match, including location, time, and contact details.
            </p>
          </div>

          {/* Match Location (like MatchCard) */}
          <div className="match-location">
            {matchData.match_type || 'FRIENDLY'}
            <div className="match-type">{matchData.location}</div>
          </div>

          {/* Teams Display - Vertical Layout */}
          <div className="teams-display-vertical">
            {/* Opposition Team (Top) */}
            <div className="team-card-vertical opposition-team">
              <div className="shield-container">
                {oppositionLogo ? (
                  <img 
                    className="team-shield" 
                    src={oppositionLogo} 
                    alt={matchData.opposition}
                    onError={(e) => {
                      e.target.src = defaultLightLogo;
                    }}
                  />
                ) : (
                  <div className="shield-placeholder">
                    <FaFutbol size={50} />
                  </div>
                )}
              </div>
              <div className="team-name">{matchData.opposition || 'Opposition Team'}</div>
            </div>

            {/* VS Divider */}
            <div className="vs-divider-vertical">vs</div>

            {/* Current Team (Bottom) */}
            <div className="team-card-vertical current-team">
              <div className="shield-container">
                <img 
                  className="team-shield" 
                  src={teamLogo} 
                  alt={matchData.team_name}
                  onError={(e) => {
                    e.target.src = defaultLightLogo;
                  }}
                />
              </div>
              <div className="team-name">{matchData.team_name}</div>
            </div>
          </div>

          {/* Match Venue (like MatchCard) */}
          <div className="match-venue">
            <div className="match-date">
              {formatDate(matchData.date)}
            </div>
            <div className="match-time">
              <div className="kickoff-time">
                <FaClock />
                {matchData.status === 'TBC' ? 'TBC' : `K.O. ${formatTime(matchData.time, matchData.status)}`}
              </div>
            </div>
          </div>

          {/* Instructions if available */}
          {/* Commented out - may be internal only
          {matchData.instructions && (
            <div className="match-instructions">
              <div className="instructions-label">Additional Instructions:</div>
              <div className="instructions-value">{matchData.instructions}</div>
            </div>
          )}
          */}

          {/* Coach Details */}
          {(matchData.coach?.first_name || matchData.coach?.email || matchData.coach?.phone) && (
            <div className="coach-details">
              <div className="coach-label">Match Organiser:</div>
              <div className="coach-info">
                {matchData.coach.first_name && matchData.coach.last_name && (
                  <div className="coach-name">
                    {matchData.coach.first_name} {matchData.coach.last_name}
                  </div>
                )}
                {matchData.coach.email && (
                  <div className="coach-email">
                    <a href={`mailto:${matchData.coach.email}`}>{matchData.coach.email}</a>
                  </div>
                )}
                {matchData.coach.phone && (
                  <div className="coach-phone">
                    <a href={`tel:${matchData.coach.phone}`}>
                      {matchData.coach.phone.startsWith('0') ? matchData.coach.phone : `0${matchData.coach.phone}`}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Google Maps Button */}
          {matchData.google_maps_url && (
            <div className="google-maps-button-container">
              <a 
                href={matchData.google_maps_url}
                target="_blank"
                rel="noopener noreferrer"
                className="google-maps-button"
              >
                <FaMapMarkerAlt className="maps-icon" />
                View Location on Google Maps
              </a>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="section-divider">
          <div className="divider-line"></div>
          <span className="divider-text">About FirstWhistle</span>
          <div className="divider-line"></div>
        </div>

        {/* FOMO Section */}
        <div className="fomo-section">
          {/* Personal Message from Dean */}
          <div className="dean-message">
            <div className="dean-avatar">
              <img src={founderImage} alt="Dean - FirstWhistle Founder" />
            </div>
            <div className="dean-content">
              <h4>Hi, I'm Dean</h4>
              <p>As a parent and football organiser, I saw the need for better communication and more meaningful player recognition. Coaches already juggle enough, so I created FirstWhistle to make matchday organisation simpler, give parents the clarity they want, and give players extra motivation and confidence off the pitch in today's digital world.</p>
              <p>If it can help make things easier for your team too, I would love you to take a look.</p>
            </div>
          </div>
          
          <h2 className="fomo-title">You're 1 click away from better team management</h2>
        </div>

        {/* Feature List - Simple Bullet Points */}
        <div className="features-section">
          <h3 className="features-title">Why other clubs use First Whistle</h3>
          <div className="features-image">
            <img 
              src="https://football.pebbledesigns.co.uk/api/images/app-3.png" 
              alt="FirstWhistle App"
            />
          </div>
          <ul className="features-list">
            <li>✓ Send match invitations and get responses in one place</li>
            <li>✓ Create engaging match reports with photos and highlights</li>
            <li>✓ Celebrate individual achievements, not just POTM</li>
            <li>✓ View player statistics and performance tracking</li>
            <li>✓ FREE forever for coaches and managers</li>
          </ul>
        </div>

        {/* Achievement Preview */}
        <div className="achievement-preview-section">
          <h3 className="preview-title">Why Players Love First Whistle</h3>

          {/* Social Proof */}
          <div className="social-proof-section">
            <div className="social-proof-card">
              <div className="quote-icon">"</div>
              <p className="testimonial">
                "I love seeing my achievements and match reports! It makes me want to work harder in every game."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">L</div>
                <div className="author-info">
                  <h4>Leland, Age 12</h4>
                  <span>Player</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Parent Testimonial */}
        <div className="parent-testimonial-section">
          <h3 className="parent-testimonial-title">Parents Love First Whistle</h3>
          <div className="social-proof-card">
            <div className="quote-icon">"</div>
            <p className="testimonial">
              "FirstWhistle has made it so easy to stay connected with my son's team. I love seeing his achievements and match reports - it really motivates him to keep improving!"
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">S</div>
              <div className="author-info">
                <h4>Sarah, Parent</h4>
                <span>U13s Team</span>
              </div>
            </div>
          </div>
        </div>


        {/* CTA Section - App Store Buttons */}
        <div className="cta-section">
          <div className="download-buttons">
            {isDesktop || isIOS ? (
              <a 
                href="https://apps.apple.com/gb/app/first-whistle-football/id6755308177?utm_source=web&utm_medium=match_welcome_page&utm_campaign=app_download"
                target="_blank"
                rel="noopener noreferrer"
                className="download-button app-store"
                data-track="app_store_download"
                data-platform="ios"
                onClick={() => {
                  // Track Apple App Store click
                  if (window.gtag) {
                    window.gtag('event', 'app_store_click', {
                      'store': 'apple',
                      'source': 'match_welcome_page'
                    });
                  }
                  // Fallback: log to console for debugging
                  console.log('App Store download clicked - iOS (Match Welcome Page)');
                }}
              >
                <img 
                  src="https://football.pebbledesigns.co.uk/api/images/btn-app-store.png" 
                  alt="Download on the App Store"
                />
                <FaChevronRight className="chevron-icon" />
              </a>
            ) : null}
            {isDesktop || isAndroid ? (
              <a 
                href="https://play.google.com/store/apps/details?id=app.firstwhistle.my&hl=en_GB&utm_source=web&utm_medium=match_welcome_page&utm_campaign=app_download"
                target="_blank"
                rel="noopener noreferrer"
                className="download-button play-store"
                data-track="play_store_download"
                data-platform="android"
                onClick={() => {
                  // Track Google Play Store click
                  if (window.gtag) {
                    window.gtag('event', 'app_store_click', {
                      'store': 'google_play',
                      'source': 'match_welcome_page'
                    });
                  }
                  // Fallback: log to console for debugging
                  console.log('App Store download clicked - Android (Match Welcome Page)');
                }}
              >
                <img 
                  src="https://football.pebbledesigns.co.uk/api/images/btn-play-store.png" 
                  alt="Get it on Google Play"
                />
                <FaChevronRight className="chevron-icon" />
              </a>
            ) : null}
          </div>
          <p className="cta-subtitle">It's FREE forever for coaches and managers</p>
        </div>

        {/* Contact Details */}
        <div className="contact-section">
          <h3>Questions?</h3>
          <p>Get in touch with Dean for any questions about FirstWhistle</p>
          <div className="contact-details">
            <div className="contact-item">
              <span className="contact-label">Email:</span>
              <a href="mailto:dean.hodges@firstwhistle.app" className="contact-link">dean.hodges@firstwhistle.app</a>
            </div>
            <div className="contact-item">
              <span className="contact-label">Phone:</span>
              <a href="tel:07875930733" className="contact-link">07875 930733</a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="welcome-footer">
          <p>Powered by FirstWhistle</p>
        </footer>
      </div>
    </div>
  );
}

export default MatchWelcomePage;

